import { useState } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2, Mic, CheckSquare, MessageSquare } from "lucide-react";

export default function Login() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res =
      mode === "login"
        ? await login(email, password)
        : await register(name, email, password);
    setLoading(false);
    if (!res.ok) setError(res.error);
  };

  return (
    <div className="min-h-screen flex bg-[#0B0C10] text-gray-100">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 relative overflow-hidden p-12 border-r border-[#2E3242]">
        <div
          className="absolute inset-0 opacity-25"
          style={{
            background:
              "radial-gradient(circle at 30% 20%, rgba(153,0,0,0.55), transparent 55%), radial-gradient(circle at 80% 80%, rgba(74,0,16,0.6), transparent 50%)",
          }}
        />
        <div className="relative z-10 flex items-center gap-3">
          <img src={LOGO_URL} alt="Recipe Remedy" className="w-12 h-12 object-contain" />
          <div>
            <p className="font-display font-bold text-lg leading-none">Recipe Remedy</p>
            <p className="text-xs text-red-400 font-mono tracking-widest">DEVHUB</p>
          </div>
        </div>

        <div className="relative z-10 max-w-md">
          <h1 className="font-display text-4xl xl:text-5xl font-extrabold tracking-tight leading-[1.05]">
            Where the build
            <br />
            <span className="text-red-500">comes together.</span>
          </h1>
          <p className="mt-5 text-gray-400 text-base leading-relaxed">
            Channels, live task sync, and an AI that turns your meeting voice notes into
            notes & tracked to-dos. Ditch the Discord scramble.
          </p>
          <div className="mt-8 space-y-3">
            {[
              { icon: MessageSquare, t: "Focused channels for every conversation" },
              { icon: CheckSquare, t: "Tasks that sync live across the whole team" },
              { icon: Mic, t: "Drop a voice note, get an instant action list" },
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-3 text-sm text-gray-300">
                <span className="w-8 h-8 rounded-lg ruby-gradient flex items-center justify-center">
                  <f.icon className="w-4 h-4 text-white" />
                </span>
                {f.t}
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 text-xs text-gray-600 font-mono">
          © 2026 Recipe Remedy — internal dev workspace
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-sm"
        >
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src={LOGO_URL} alt="logo" className="w-11 h-11 object-contain" />
            <div>
              <p className="font-display font-bold">Recipe Remedy</p>
              <p className="text-xs text-red-400 font-mono tracking-widest">DEVHUB</p>
            </div>
          </div>

          <h2 className="font-display text-2xl font-bold mb-1">
            {mode === "login" ? "Welcome back" : "Create your account"}
          </h2>
          <p className="text-sm text-gray-400 mb-6">
            {mode === "login"
              ? "Sign in to jump into the workspace."
              : "Join the Recipe Remedy dev team."}
          </p>

          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <div>
                <Label className="text-gray-300">Name</Label>
                <Input
                  data-testid="register-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ada Lovelace"
                  className="mt-1.5 bg-[#16181F] border-[#2E3242] focus-visible:ring-red-600"
                  required
                />
              </div>
            )}
            <div>
              <Label className="text-gray-300">Email</Label>
              <Input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@reciperemedy.dev"
                className="mt-1.5 bg-[#16181F] border-[#2E3242] focus-visible:ring-red-600"
                required
              />
            </div>
            <div>
              <Label className="text-gray-300">Password</Label>
              <Input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1.5 bg-[#16181F] border-[#2E3242] focus-visible:ring-red-600"
                required
              />
            </div>

            {error && (
              <p data-testid="auth-error" className="text-sm text-red-400 bg-red-950/40 border border-red-900/50 rounded-lg px-3 py-2">
                {error}
              </p>
            )}

            <Button
              data-testid="auth-submit-button"
              type="submit"
              disabled={loading}
              className="w-full ruby-gradient hover:opacity-90 text-white font-semibold h-11 border border-red-900/40 ember-glow"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : mode === "login" ? (
                "Sign in"
              ) : (
                "Create account"
              )}
            </Button>
          </form>

          <p className="mt-6 text-sm text-gray-400 text-center">
            {mode === "login" ? "New here?" : "Already have an account?"}{" "}
            <button
              data-testid="toggle-auth-mode"
              onClick={() => {
                setMode(mode === "login" ? "register" : "login");
                setError("");
              }}
              className="text-red-400 hover:text-red-300 font-medium"
            >
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </p>

          {mode === "login" && (
            <p className="mt-4 text-xs text-gray-600 text-center font-mono">
              demo: admin@reciperemedy.dev / remedy123
            </p>
          )}
        </motion.div>
      </div>
    </div>
  );
}
